import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';
import { INotice, INoticeReader, INoticeRequest } from '../interfaces/notice.interface';

interface Page<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}

export const NoticeService = {
    create: async (institutionId: string, request: INoticeRequest, attachment?: File | null): Promise<INotice> => {
        const formData = new FormData();
        formData.append('request', new Blob([JSON.stringify(request)], { type: 'application/json' }));
        if (attachment) formData.append('attachment', attachment);
        const response = await axios.post(ENDPOINTS.NOTICE.CREATE(institutionId), formData, {
            headers: {
                ...AuthService.getAuthHeader(),
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    update: async (noticeId: string, institutionId: string, request: INoticeRequest): Promise<INotice> => {
        const response = await axios.put(ENDPOINTS.NOTICE.UPDATE(noticeId, institutionId), request, {
            headers: { ...AuthService.getAuthHeader(), 'Content-Type': 'application/json' },
        });
        return response.data;
    },

    delete: async (noticeId: string, institutionId: string): Promise<void> => {
        await axios.delete(ENDPOINTS.NOTICE.DELETE(noticeId, institutionId), {
            headers: AuthService.getAuthHeader(),
        });
    },

    listForAdmin: async (institutionId: string, page = 0, size = 20): Promise<Page<INotice>> => {
        const response = await axios.get(ENDPOINTS.NOTICE.ADMIN_LIST(institutionId), {
            headers: AuthService.getAuthHeader(),
            params: { page, size },
        });
        return response.data;
    },

    feed: async (institutionId: string, page = 0, size = 20): Promise<Page<INotice>> => {
        const response = await axios.get(ENDPOINTS.NOTICE.FEED(institutionId), {
            headers: AuthService.getAuthHeader(),
            params: { page, size },
        });
        return response.data;
    },

    getById: async (noticeId: string): Promise<INotice> => {
        const response = await axios.get(ENDPOINTS.NOTICE.GET(noticeId), {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    markRead: async (noticeId: string): Promise<void> => {
        await axios.post(ENDPOINTS.NOTICE.MARK_READ(noticeId), null, {
            headers: AuthService.getAuthHeader(),
        });
    },

    getReaders: async (noticeId: string, institutionId: string): Promise<INoticeReader[]> => {
        const response = await axios.get(ENDPOINTS.NOTICE.READERS(noticeId, institutionId), {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    downloadAttachment: async (noticeId: string, fileName: string): Promise<void> => {
        const response = await axios.get(ENDPOINTS.NOTICE.ATTACHMENT(noticeId), {
            headers: AuthService.getAuthHeader(),
            responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },
};
